import { FaGithub, FaLinkedin } from 'react-icons/fa6';
import { githubHandle } from '@/lib/utils';
import type { LandingMentor } from '@/types';
import type { LandingVariant, LandingVariantProps } from './landing-variant';

function normalizeLinkedin(value: string): string {
  if (value.startsWith('http')) return value;
  const stripped = value.replace(/^\/?(in\/)?/, '');
  return `https://www.linkedin.com/in/${stripped}`;
}

const STYLES: Record<
  LandingVariant,
  {
    section: string;
    heading: string;
    headingChip: string;
    count: string;
    card: string;
    photo: string;
    meta: string;
    name: string;
    company: string;
    icon: string;
  }
> = {
  ph26: {
    section:
      'theme-ph26 selection-on-light flex w-full flex-col items-center justify-center bg-background px-6 py-16 text-primary sm:px-10 md:py-24',
    heading:
      'text-center font-title text-5xl leading-tight sm:text-6xl md:text-7xl',
    headingChip:
      'inline-block bg-primary px-3 py-1 text-background sm:px-4 sm:py-1.5',
    count: 'mt-4 text-center font-mono text-primary/70 text-sm md:text-base',
    card: 'group flex flex-col border-2 border-primary bg-background',
    photo: 'relative aspect-square w-full overflow-hidden bg-primary/10',
    meta: 'flex flex-1 flex-col gap-1 border-primary border-t-2 px-3 py-3',
    name: 'font-title text-primary text-sm leading-tight md:text-base',
    company: 'font-mono text-[11px] text-primary/70 leading-snug md:text-xs',
    icon: 'text-primary/85 transition hover:text-primary',
  },
  ar: {
    section:
      'selection-on-light flex w-full flex-col items-center justify-center bg-primary px-6 py-16 text-background sm:px-10 md:py-24',
    heading:
      'text-center font-bold font-title text-5xl leading-tight sm:text-6xl md:text-7xl',
    headingChip:
      'inline-block bg-background px-3 py-1 text-primary sm:px-4 sm:py-1.5',
    count: 'mt-4 text-center font-mono text-background/70 text-sm md:text-base',
    card: 'group flex flex-col border-2 border-background bg-primary',
    photo: 'relative aspect-square w-full overflow-hidden bg-background/10',
    meta: 'flex flex-1 flex-col gap-1 border-background border-t-2 px-3 py-3',
    name: 'font-bold font-title text-background text-sm leading-tight md:text-base',
    company: 'font-mono text-[11px] text-background/70 leading-snug md:text-xs',
    icon: 'text-background/85 transition hover:text-background',
  },
};

// `_components/mentors-slide.tsx`. The server component's query is the controller's
// (LandingEditions.mentors_for), so this renders nothing when the edition has no mentors
// — the same `return null` as over there.
//
// The three Next copies differ only in palette and in how they turn `mentor.github` into
// a URL: Bogotá normalises it with `githubHandle`, the other two paste it in raw, which
// breaks the avatar and the profile link when the column holds a full URL. The one
// implementation keeps the working behaviour (guardrail 600).
export function MentorsSlide({
  mentors,
  variant,
}: { mentors: LandingMentor[] } & LandingVariantProps) {
  const styles = STYLES[variant];

  if (mentors.length === 0) return null;

  return (
    <section className={styles.section}>
      <div className="mx-auto w-full max-w-6xl">
        <h2 className={styles.heading}>
          <span className={styles.headingChip}>top mentors</span>
        </h2>
        <p className={styles.count}>
          {mentors.length} mentores crack acompañándote
        </p>

        <ul className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {mentors.map((mentor) => {
            const handle = githubHandle(mentor.github);
            const imageUrl =
              mentor.pictureUrl ??
              `https://avatars.githubusercontent.com/${handle}`;
            const githubUrl = `https://github.com/${handle}`;
            const linkedinUrl = mentor.linkedin
              ? normalizeLinkedin(mentor.linkedin)
              : null;

            return (
              <li key={mentor.id} className={styles.card}>
                <div className={styles.photo}>
                  <img
                    src={imageUrl}
                    alt={mentor.fullName}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover grayscale transition duration-500 ease-out group-hover:grayscale-0"
                  />
                </div>
                <div className={styles.meta}>
                  <p className={styles.name}>{mentor.fullName}</p>
                  {mentor.companyTitle ? (
                    <p className={styles.company}>{mentor.companyTitle}</p>
                  ) : null}
                  <div className="mt-2 flex items-center gap-3">
                    <a
                      href={githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`GitHub de ${mentor.fullName}`}
                      className={styles.icon}
                    >
                      <FaGithub className="size-4 md:size-5" />
                    </a>
                    {linkedinUrl ? (
                      <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`LinkedIn de ${mentor.fullName}`}
                        className={styles.icon}
                      >
                        <FaLinkedin className="size-4 md:size-5" />
                      </a>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
